import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { compressImage } from "@/lib/imageCompress";
import { Plus, Inbox, Paperclip, X, Megaphone, Check } from "lucide-react";

function fmt(iso) { return iso ? new Date(iso).toLocaleString() : ""; }

function MemoDetail({ id, onBack, onChanged, user }) {
  const [memo, setMemo] = useState(null);
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await api.get(`/memos/${id}`); setMemo(r.data);
    } catch (e) { toast.error(formatApiError(e)); onBack(); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const submitConfirm = async () => {
    setBusy(true);
    try {
      await api.post(`/memos/${id}/confirm`, { remarks: confirmText.trim() || null });
      toast.success("Acknowledgement recorded"); setConfirmText(""); await load(); onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  if (!memo) return <div className="p-8 text-center text-slate-500">Loading…</div>;

  const isOwnerTier = user?.role === "school_owner" || user?.role === "sub_owner";
  const isBranchMember = user?.role === "branch_admin"
    && (user.branch_ids || []).some((b) => (memo.target_branch_ids || []).includes(b));

  return (
    <div className="space-y-5" data-testid="memo-detail">
      <button onClick={onBack} className="text-sm text-[#1D4ED8] hover:underline">&larr; Back to memos</button>
      <Card className="p-6 rounded-3xl border-[#2563EB]/10 space-y-3">
        <div className="text-xs text-slate-500">From {memo.creator_name} • {fmt(memo.created_at)}</div>
        <h2 className="font-heading text-2xl font-bold">{memo.title}</h2>
        <p className="whitespace-pre-wrap text-sm text-slate-700">{memo.body}</p>
      </Card>

      {isBranchMember && !memo.my_confirmation && (
        <Card className="p-6 rounded-3xl border-sky-200 bg-sky-50/30 space-y-3" data-testid="memo-confirm-panel">
          <h3 className="font-heading font-bold">Acknowledge this memo</h3>
          <Textarea rows={3} value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder="Your remarks / feedback (optional)." data-testid="memo-confirm-input" />
          <Button onClick={submitConfirm} disabled={busy} className="rounded-full bg-[#2563EB]" data-testid="memo-confirm-submit">
            <Check className="w-4 h-4 mr-1.5" /> Acknowledge
          </Button>
        </Card>
      )}

      {isBranchMember && memo.my_confirmation && (
        <Card className="p-4 rounded-3xl border-emerald-200 bg-emerald-50/30 text-sm text-emerald-800">
          You acknowledged this memo on {fmt(memo.my_confirmation.confirmed_at)}.
          {memo.my_confirmation.remarks && <div className="mt-1 italic">"{memo.my_confirmation.remarks}"</div>}
        </Card>
      )}

      {isOwnerTier && memo.confirmations_summary && (
        <Card className="p-6 rounded-3xl border-[#2563EB]/10 space-y-4" data-testid="memo-confirmations-summary">
          <div className="flex items-center justify-between">
            <h3 className="font-heading font-bold">Acknowledgements</h3>
            <span className="text-sm text-slate-600">{memo.confirmations_summary.total_confirmed} / {memo.confirmations_summary.total_required}</span>
          </div>
          {memo.confirmations_summary.branches.map((b) => (
            <div key={b.branch_id} className="border border-slate-100 rounded-2xl p-4" data-testid={`memo-branch-${b.branch_id}`}>
              <div className="flex items-center justify-between mb-3">
                <span className="font-heading font-semibold">{b.branch_name}</span>
                <span className="text-xs text-slate-500">{b.confirmed_count}/{b.required_count}</span>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <div className="text-xs uppercase font-semibold text-emerald-700 mb-1">Confirmed</div>
                  {b.confirmed.length === 0 && <div className="text-xs text-slate-400">No one yet</div>}
                  {b.confirmed.map((u) => (
                    <div key={u.id} className="text-xs py-0.5">
                      <span className="font-medium">{u.name}</span>
                      {u.remarks && <span className="text-slate-500"> — "{u.remarks}"</span>}
                    </div>
                  ))}
                </div>
                <div>
                  <div className="text-xs uppercase font-semibold text-amber-700 mb-1">Pending</div>
                  {b.pending.length === 0 && <div className="text-xs text-slate-400">Everyone has confirmed 🎉</div>}
                  {b.pending.map((u) => <div key={u.id} className="text-xs py-0.5 text-slate-600">{u.name}</div>)}
                </div>
              </div>
            </div>
          ))}
        </Card>
      )}
    </div>
  );
}

function MemoCreate({ onBack, onCreated, user }) {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [allBranches, setAllBranches] = useState(true);
  const [pickIds, setPickIds] = useState([]);
  const [branches, setBranches] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/branches/my");
        setBranches((r.data || []).filter((b) => b.status === "approved"));
      } catch { /* ignore */ }
    })();
  }, []);

  const togglePick = (bid) =>
    setPickIds((arr) => arr.includes(bid) ? arr.filter((x) => x !== bid) : [...arr, bid]);

  const addFile = async (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    if (f.type === "application/pdf") {
      const b64 = await new Promise((resolve, reject) => {
        const r = new FileReader(); r.onerror = reject; r.onload = () => resolve(r.result); r.readAsDataURL(f);
      });
      setAttachments((arr) => [...arr, { mime: "application/pdf", base64: b64, name: f.name }]);
    } else if (f.type.startsWith("image/")) {
      try {
        const dataUrl = await compressImage(f, { maxKB: 400, maxDim: 1600 });
        setAttachments((arr) => [...arr, { mime: "image/jpeg", base64: dataUrl, name: f.name }]);
      } catch (err) { toast.error(err.message || "Could not process image"); }
    } else { toast.error("Only images or PDFs are allowed"); }
  };

  const submit = async () => {
    if (!title.trim() || !body.trim()) return toast.error("Title and body are required");
    if (!allBranches && pickIds.length === 0) return toast.error("Pick at least one branch or toggle All branches");
    setBusy(true);
    try {
      const res = await api.post("/memos", { title: title.trim(), body: body.trim(), all_branches: allBranches, branch_ids: allBranches ? [] : pickIds, attachments });
      toast.success(`Memo sent — ${res.data?.recipients_notified || 0} recipient(s) notified`);
      onCreated();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Card className="p-6 rounded-3xl border-[#2563EB]/10 space-y-4" data-testid="memo-create">
      <button onClick={onBack} className="text-sm text-[#1D4ED8] hover:underline self-start">&larr; Back</button>
      <h2 className="font-heading text-2xl font-bold">Issue a memo</h2>
      <div>
        <Label>Title</Label>
        <Input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} data-testid="memo-title-input" />
      </div>
      <div>
        <Label>Memo body</Label>
        <Textarea rows={8} value={body} onChange={(e) => setBody(e.target.value)} placeholder="Write the full memo here. Be as detailed as needed." maxLength={8000} data-testid="memo-body-input" />
      </div>
      <div>
        <Label>Send to</Label>
        <label className="flex items-center gap-2 mt-1 text-sm">
          <input type="checkbox" checked={allBranches} onChange={(e) => setAllBranches(e.target.checked)} data-testid="memo-all-branches" />
          All branches
        </label>
        {!allBranches && (
          <div className="grid sm:grid-cols-2 gap-2 mt-2 max-h-60 overflow-y-auto">
            {branches.map((b) => (
              <label key={b.id} className="flex items-center gap-2 text-sm border border-slate-200 rounded-2xl px-3 py-2 cursor-pointer hover:bg-slate-50">
                <input type="checkbox" checked={pickIds.includes(b.id)} onChange={() => togglePick(b.id)} />
                {b.name}
              </label>
            ))}
          </div>
        )}
      </div>
      <div>
        <Label>Attachments (optional — images or PDF)</Label>
        <div className="flex items-center gap-2 mt-1 flex-wrap">
          <input id="memo-file" type="file" accept="image/*,application/pdf" onChange={addFile} className="hidden" data-testid="memo-file-input" />
          <label htmlFor="memo-file" className="cursor-pointer inline-flex items-center gap-2 px-3 py-2 rounded-full bg-slate-100 text-sm hover:bg-slate-200">
            <Paperclip className="w-4 h-4" /> Add file
          </label>
          {attachments.map((a, i) => (
            <span key={i} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-50 rounded-full text-xs">
              {a.name}
              <button onClick={() => setAttachments((arr) => arr.filter((_, j) => j !== i))} className="text-rose-500"><X className="w-3 h-3" /></button>
            </span>
          ))}
        </div>
      </div>
      <Button onClick={submit} disabled={busy} className="rounded-full bg-[#2563EB] hover:bg-[#1D4ED8] w-full sm:w-auto" data-testid="memo-submit-btn">
        <Megaphone className="w-4 h-4 mr-1.5" />Send memo
      </Button>
    </Card>
  );
}

export default function MemosPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState({ mode: "list" });
  const navigate = useNavigate();

  const canCreate = user?.role === "school_owner"
    || (user?.role === "sub_owner" && (user.permissions || []).includes("memos_manage"))
    || (user?.role === "branch_admin" && (user.permissions || {}).memos_create);

  const load = async () => {
    setLoading(true);
    try { setItems((await api.get("/memos")).data?.items || []); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    const m = window.location.pathname.match(/\/memos\/([^/]+)$/);
    if (m) setView({ mode: "detail", id: m[1] });
  // eslint-disable-next-line
  }, []);

  const open = (id) => { setView({ mode: "detail", id }); navigate(`/memos/${id}`); };
  const back = () => { setView({ mode: "list" }); navigate("/memos"); load(); };

  return (
    <>
      <div className="space-y-5" data-testid="memos-page">
        {view.mode === "list" && (
          <>
            <div className="flex flex-wrap items-end justify-between gap-3">
              <div>
                <p className="text-xs text-slate-400 uppercase font-heading">Communications</p>
                <h1 className="font-heading text-3xl font-bold">Memos</h1>
                <p className="text-sm text-brand-inkMuted mt-1">Owner-issued announcements with branch acknowledgement tracking.</p>
              </div>
              {canCreate && (
                <Button onClick={() => setView({ mode: "create" })} className="rounded-full bg-[#2563EB] hover:bg-[#1D4ED8]" data-testid="memo-new-btn">
                  <Plus className="w-4 h-4 mr-1.5" /> New memo
                </Button>
              )}
            </div>
            {loading && <p className="text-center text-slate-500 py-8">Loading…</p>}
            {!loading && items.length === 0 && (
              <Card className="p-10 rounded-3xl text-center">
                <Inbox className="w-10 h-10 mx-auto text-slate-300 mb-3" />
                <p className="font-heading font-bold">No memos yet</p>
                <p className="text-sm text-slate-500 mt-1">{canCreate ? "Tap New memo to publish one." : "Nothing to acknowledge."}</p>
              </Card>
            )}
            <div className="space-y-3">
              {items.map((m) => (
                <button key={m.id} onClick={() => open(m.id)} className="w-full text-left bg-white border border-[#2563EB]/10 rounded-3xl p-5 hover:-translate-y-0.5 hover:shadow-md transition-all" data-testid={`memo-row-${m.id}`}>
                  <div className="text-xs text-slate-500">From {m.creator_name} • {fmt(m.created_at)} • {(m.target_branch_ids || []).length} branch(es)</div>
                  <div className="font-heading font-bold text-foreground mt-0.5 truncate">{m.title}</div>
                </button>
              ))}
            </div>
          </>
        )}
        {view.mode === "create" && <MemoCreate onBack={back} onCreated={back} user={user} />}
        {view.mode === "detail" && <MemoDetail id={view.id} onBack={back} onChanged={load} user={user} />}
      </div>
    </>
  );
}
